import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Pagination helpers (inlined — no external common schema needed)
// ────────────────────────────────────────────────────────────────────────────

const PaginationSchema = z.object({
  lastKey: z.string().optional().nullable(),
  limit: z.number().int().min(1).max(1000).default(200),
  nextToken: z.string().optional().nullable(),
});

const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    count: z.number().int().min(0),
    items: z.array(itemSchema),
    lastKey: z.string().optional().nullable(),
    nextToken: z.string().optional().nullable(),
  });

// ────────────────────────────────────────────────────────────────────────────
// Invoice
// ────────────────────────────────────────────────────────────────────────────

export const InvoiceStatusSchema = z.enum([
  'Open',
  'Past Due',
  'Credit',
  'Closed',
]);

export type IInvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceLineItemSchema = z.object({
  bolNumber: z.string().nullable(),
  contractNumber: z.string().nullable(),
  line: z.number(),
  poNumber: z.string().nullable(),
  prepayAmount: z.number(),
  productAmount: z.number(),
  productName: z.string(),
  qty: z.number().nullable(),
  releaseNumber: z.string().nullable(),
  tax: z.number(),
  totalAmount: z.number(),
  unitPrice: z.number().nullable(),
  uom: z.string().nullable(),
});

export type IInvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;

export const InvoiceSchema = z.object({
  amountDue: z.number(),
  currency: z.string(),
  dueDate: z.string(),
  id: z.string(),
  invoiceAmount: z.number(),
  invoiceDate: z.string(),
  kochParty: z.string().nullable(),
  lineItems: z.array(InvoiceLineItemSchema),
  paymentsApplied: z.number(),
  status: InvoiceStatusSchema,
});

export type IInvoice = z.infer<typeof InvoiceSchema>;

export const InvoiceListQuerySchema = PaginationSchema.extend({
  bolNumber: z.string().optional(),
  contractNumber: z.string().optional(),
  kochParty: z.string().optional(),
  orderReleaseNumber: z.string().optional(),
  poNumber: z.string().optional(),
  status: InvoiceStatusSchema.optional(),
});

export type IInvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;

export const InvoiceListResponseSchema = PaginatedResponseSchema(InvoiceSchema);

export type IInvoiceListResponse = z.infer<typeof InvoiceListResponseSchema>;
